import { isPlatformBrowser } from '@angular/common';
import { computed, effect, Inject, Injectable, PLATFORM_ID, Signal, signal, WritableSignal } from '@angular/core';

import { Observable, Subject } from 'rxjs';
import { parse } from 'tldts';

import { LayoutConfig, LayoutState, MenuChangeEvent } from '@core/type/layout';
import { CookieService } from './cookie.service';

@Injectable({
  providedIn: 'root',
})
export class LayoutService {
  public menuSourceObservable: Observable<MenuChangeEvent>;
  public resetSourceObservable: Observable<unknown>;
  public configUpdateObservable: Observable<LayoutConfig>;
  public overlayOpenObservable: Observable<unknown>;

  public layoutConfig: WritableSignal<LayoutConfig>;
  public layoutState: WritableSignal<LayoutState>;
  public isDarkTheme: Signal<boolean> = computed(() => this.layoutConfig().darkTheme);
  public isSidebarActive: Signal<boolean> = computed(
    () =>
      this.layoutState().overlayMenuActive ||
      this.layoutState().staticMenuMobileActive ||
      this.layoutState().overlaySubmenuActive
  );
  public isOverlay: Signal<boolean> = computed(() => this.layoutConfig().menuMode === 'overlay');
  public isStatic: Signal<boolean> = computed(() => this.layoutConfig().menuMode === 'static');
  public isReveal: Signal<boolean> = computed(() => this.layoutConfig().menuMode === 'reveal');

  public transitionComplete: WritableSignal<boolean> = signal<boolean>(false);

  private initialized: boolean = false;

  private readonly configUpdate: Subject<LayoutConfig> = new Subject<LayoutConfig>();
  private readonly overlayOpen: Subject<unknown> = new Subject<unknown>();
  private readonly menuSource: Subject<MenuChangeEvent> = new Subject<MenuChangeEvent>();
  private readonly resetSource: Subject<unknown> = new Subject<unknown>();

  private config: LayoutConfig = {
    darkTheme: false,
    menuMode: 'static',
  };

  private readonly bodyBackgroundPalette = {
    light: 'linear-gradient(180deg, #e0e7f5 0%, rgba(170, 194, 239, 0.06) 111.26%)',
    dark: '#000C23',
  };

  private readonly state: LayoutState = {
    staticMenuDesktopInactive: false,
    overlayMenuActive: false,
    configSidebarVisible: false,
    staticMenuMobileActive: false,
    menuHoverActive: false,
    searchBarActive: false,
    sidebarActive: false,
    anchored: false,
    overlaySubmenuActive: false,
  };

  constructor(
    @Inject(PLATFORM_ID) private readonly platform: string,
    private readonly cookieService: CookieService
  ) {
    const savedConfig = this.cookieService.getAsJson<LayoutConfig>('layout-config');
    const savedState = this.cookieService.getAsJson<LayoutState>('layout-state');

    if (savedConfig) {
      this.config = {
        darkTheme: savedConfig.darkTheme,
        menuMode: savedConfig.menuMode,
      };
    }

    if (savedState) {
      this.state = {
        staticMenuDesktopInactive: false,
        overlayMenuActive: false,
        configSidebarVisible: savedState.configSidebarVisible,
        staticMenuMobileActive: savedState.staticMenuMobileActive,
        menuHoverActive: savedState.menuHoverActive,
        searchBarActive: savedState.searchBarActive,
        sidebarActive: savedState.sidebarActive,
        anchored: savedState.anchored,
        overlaySubmenuActive: savedState.overlaySubmenuActive,
      };
    }

    this.layoutConfig = signal(this.config);
    this.layoutState = signal(this.state);

    this.menuSourceObservable = this.menuSource.asObservable();
    this.resetSourceObservable = this.resetSource.asObservable();
    this.configUpdateObservable = this.configUpdate.asObservable();
    this.overlayOpenObservable = this.overlayOpen.asObservable();

    effect(() => {
      const config = this.layoutConfig();
      if (config) {
        this.onConfigUpdate();
      }
    });

    effect(() => {
      const state = this.layoutState();

      if (!state) {
        return;
      }

      if (isPlatformBrowser(this.platform)) {
        const domain = parse(globalThis.location.hostname).domain ?? '';

        this.cookieService.setAsJson('layout-state', state, {
          expires: 365,
          path: '/',
          domain: `.${domain}`,
          sameSite: 'strict',
        });
      }
    });

    effect(() => {
      const config = this.layoutConfig();

      if (!this.initialized || !config) {
        this.initialized = true;
        return;
      }
      this.handleDarkModeTransition(config);
    });
  }

  private handleDarkModeTransition(config: LayoutConfig): void {
    const supportsViewTransition = 'startViewTransition' in document;

    if (supportsViewTransition) {
      this.startViewTransition(config);
    } else {
      this.toggleDarkMode(config);
      this.onTransitionEnd();
    }
  }

  private startViewTransition(config: LayoutConfig): void {
    const transition = document.startViewTransition(() => {
      this.toggleDarkMode(config);
    });

    transition.ready
      .then(() => {
        this.onTransitionEnd();
      })
      .catch(() => {});
  }

  public toggleDarkMode(config?: LayoutConfig): void {
    const _config = config || this.layoutConfig();
    if (_config.darkTheme) {
      document.documentElement.dataset.theme = 'dark';
    } else {
      document.documentElement.dataset.theme = 'light';
    }
  }

  private onTransitionEnd(): void {
    this.transitionComplete.set(true);
    setTimeout(() => this.transitionComplete.set(false));
  }

  public onMenuToggle(): void {
    if (this.isOverlay()) {
      this.layoutState.update(prev => ({ ...prev, overlayMenuActive: !prev.overlayMenuActive }));

      if (this.layoutState().overlayMenuActive) {
        this.overlayOpen.next(null);
      }
    }

    if (this.isDesktop()) {
      this.layoutState.update(prev => ({
        ...prev,
        staticMenuDesktopInactive: !prev.staticMenuDesktopInactive,
      }));
    } else {
      this.layoutState.update(prev => ({
        ...prev,
        staticMenuMobileActive: !this.layoutState().staticMenuMobileActive,
      }));

      if (this.layoutState().staticMenuMobileActive) {
        this.overlayOpen.next(null);
      }
    }
  }

  private onConfigUpdate(): void {
    this.config = { ...this.layoutConfig() };
    this.configUpdate.next(this.layoutConfig());
    if (isPlatformBrowser(this.platform)) {
      const domain = parse(globalThis.location.hostname).domain ?? '';

      this.cookieService.setAsJson('layout-config', this.config, {
        expires: 365,
        path: '/',
        domain: `.${domain}`,
        sameSite: 'strict',
      });
    }
  }

  public onMenuStateChange(event: MenuChangeEvent): void {
    this.menuSource.next(event);
  }

  public reset(): void {
    this.resetSource.next(true);
  }

  public onOverlaySubmenuOpen(): void {
    this.overlayOpen.next(null);
  }

  public isDesktop(): boolean {
    if (isPlatformBrowser(this.platform)) {
      return window?.innerWidth > 991;
    }
    return true;
  }

  public isMobile(): boolean {
    return !this.isDesktop();
  }

  public updateBodyBackground() {
    const root = document.documentElement;
    const colorScheme: string = this.isDarkTheme() ? this.bodyBackgroundPalette.dark : this.bodyBackgroundPalette.light;
    root.style.setProperty('--surface-ground', colorScheme);
  }

  public hideConfigSidebar() {
    this.layoutState.update(prev => ({ ...prev, configSidebarVisible: false }));
  }
}
